import React, { useState } from 'react';
import styles from './GroupRow.module.scss';
import classNames from 'classnames';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import Menu from '../atoms/Menu';
import AssignProgramDialog from '../Programs/AssignProgramDialog';
import AssignProgramOptionsDialog from '../Programs/AssignProgramOptionsDialog';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';

import type { Group, AssignedProgram } from '../../types';

interface GroupRowProps {
  group: Group;
  selected: boolean;
  onSelect?: () => void;
  openEditGroupModal: () => void;
  deleteGroup: (group: Group) => Promise<void>;
  allowDelete: boolean;
  onEditProgram: () => void;
  onCreateProgram: () => void;
  loadGroupDetails: (group: Group) => Promise<Group | null>;
  onAssignProgramFromTemplate: (
    group: Group,
    program: AssignedProgram,
  ) => Promise<void>;
}

const GroupRow: React.FC<GroupRowProps> = ({
  group,
  onSelect,
  selected,
  deleteGroup,
  openEditGroupModal,
  allowDelete,
  onEditProgram,
  onCreateProgram,
  loadGroupDetails,
  onAssignProgramFromTemplate,
}) => {
  const [isDeleteGroupConfirmationOpen, setIsDeleteGroupConfirmationOpen] =
    useState(false);
  const [isAssignProgramOptionsModalOpen, setIsAssignProgramOptionsModalOpen] =
    useState(false);
  const [isAddProgramTemplateModalOpen, setIsAddProgramTemplateModalOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const renderGroupName = () => (
    <div className={classNames(styles.groupColumn, styles.groupName)}>
      {group.name}
    </div>
  );

  const renderTrainerName = () => (
    <div className={classNames(styles.groupColumn)}>{group.trainerName ?? ''}</div>
  );

  const renderClients = () => {
    return (
      <div
        className={classNames(styles.groupColumn)}
      >{`${group.memberCount ?? 0} Members`}</div>
    );
  };

  const renderProgram = () => {
    return (
      <div className={classNames(styles.groupColumn)}>{group.programName}</div>
    );
  };

  const handleEditGroup = () => {
    openEditGroupModal();
    handleCloseMenuOptions();
  };

  const handleDeleteGroup = async () => {
    handleCloseMenuOptions();
    await deleteGroup(group);
    setIsDeleteGroupConfirmationOpen(false);
  };

  const handleSubmitProgramTemplate = async (program: AssignedProgram) => {
    const loadedGroup = await loadGroupDetails(group);
    if (!loadedGroup) return;
    setIsAddProgramTemplateModalOpen(false);
    await onAssignProgramFromTemplate(loadedGroup, program);
  };

  const renderProgramTemplateDialog = () => {
    if (!isAddProgramTemplateModalOpen) return null;
    return (
      <AssignProgramDialog
        onSubmit={handleSubmitProgramTemplate}
        isOpen={isAddProgramTemplateModalOpen}
        onClose={() => setIsAddProgramTemplateModalOpen(false)}
      />
    );
  };

  const renderAssignProgramOptionsDialog = () => {
    if (!isAssignProgramOptionsModalOpen) return null;
    return (
      <AssignProgramOptionsDialog
        onCreate={() => {
          setIsAssignProgramOptionsModalOpen(false);
          onCreateProgram();
        }}
        onAdd={() => {
          setIsAssignProgramOptionsModalOpen(false);
          setIsAddProgramTemplateModalOpen(true);
        }}
        onClose={() => setIsAssignProgramOptionsModalOpen(false)}
      />
    );
  };

  const renderMoreOptions = () => {
    const menuOptions = [
      {
        onClick: handleEditGroup,
        label: 'Manage Group',
        icon: <BorderColorOutlinedIcon color="primary" />,
      },
      {
        onClick: () => {
          handleCloseMenuOptions();
          setIsAssignProgramOptionsModalOpen(true);
        },
        label: 'Assign Program',
        icon: <PersonAddAlt1OutlinedIcon color="primary" />,
      },
    ];

    if (group.programName || group.program) {
      menuOptions.push({
        onClick: () => {
          handleCloseMenuOptions();
          onEditProgram();
        },
        label: 'Edit Program',
        icon: <AppRegistrationIcon color="primary" />,
      });
    }

    if (allowDelete) {
      menuOptions.push({
        onClick: () => {
          handleCloseMenuOptions();
          setIsDeleteGroupConfirmationOpen(true);
        },
        label: 'Delete Group',
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      });
    }

    return (
      <div className={classNames(styles.groupColumn, styles.options)}>
        <PendingOutlinedIcon
          className={styles.optionsBtn}
          color="primary"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget as unknown as HTMLElement);
          }}
        />
        <Menu
          anchorEl={anchorEl}
          isOpen={open}
          onClose={handleCloseMenuOptions}
          options={menuOptions}
        />
      </div>
    );
  };

  const renderDeleteGroupConfirmationDialog = () => (
    <ConfirmationDialog
      text="Are you sure you want to delete this group?"
      onSubmit={handleDeleteGroup}
      submitText="Yes"
      cancelText="No"
      isOpen={isDeleteGroupConfirmationOpen}
      onCancel={() => setIsDeleteGroupConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const handleRowClick = () => {
    // Don't trigger row selection if menu is open
    // Let the menu close naturally when clicking away
    if (!open && onSelect) {
      onSelect();
    }
  };

  return (
    <>
      <div
        onClick={handleRowClick}
        className={classNames(styles.groupRow, styles.listGrid, {
          [styles.selected]: selected,
        })}
      >
        {renderGroupName()}
        {renderClients()}
        {renderTrainerName()}
        {renderProgram()}
        {renderMoreOptions()}
      </div>
      {renderDeleteGroupConfirmationDialog()}
      {renderAssignProgramOptionsDialog()}
      {renderProgramTemplateDialog()}
    </>
  );
};

export default GroupRow;
